/**
 * _                           _     ____             _    _
  | |    __ _ _   _ _ __   ___| |__ |  _ \  __ _ _ __| | _| |_   _
  | |   / _` | | | | '_ \ / __| '_ \| | | |/ _` | '__| |/ / | | | |
  | |__| (_| | |_| | | | | (__| | | | |_| | (_| | |  |   <| | |_| |
  |_____\__,_|\__,_|_| |_|\___|_| |_|____/ \__,_|_|  |_|\_\_|\__, |
                                                             |___/
 */

import { Inject, Injectable } from '@angular/core';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { BehaviorSubject } from 'rxjs';
import { ILaunchDarklyConfig } from 'src/app/config.interface';
import { LAUNCH_DARKLY } from 'src/app/tokens';
import { ELaunchDarklyFlag } from '../../constants/pol.enums';

export type LDVariation = boolean | string | number | object;

@Injectable({ providedIn: 'root' })
export class LaunchDarklyService {
  private context: LDClient.LDContext = {
    kind: 'user',
    anonymous: true
  };

  private client: LDClient.LDClient;
  public isReady$ = new BehaviorSubject<boolean>(false);

  // default values for feature flags
  public readonly flags$ = new BehaviorSubject({
    [ELaunchDarklyFlag.ClinicalModule]: false,
    [ELaunchDarklyFlag.ReleaseRemoveNavinet]: false,
    [ELaunchDarklyFlag.ClaimsBillingModule]: false,
  });

  constructor(
    @Inject(LAUNCH_DARKLY) private envConfig: ILaunchDarklyConfig
  ) {
    this.client = LDClient.initialize(this.envConfig.clientId, this.context);

    // will be executed when LD is done retrieving values
    this.client.on('ready', () => {
      // populate the BehaviorSubject with all the values
      for (const prop in ELaunchDarklyFlag) {
        const values = this.flags$.value;
        values[ELaunchDarklyFlag[prop]] = this.getFeatureFlag(ELaunchDarklyFlag[prop], null);
        this.flags$.next(values);
      }

      this.isReady$.next(true);

      // **
      // the LaunchDarklyResolver depends on this BehaviorSubject NOT being completed
      // if the BehaviorSubject is completed then the resolver will not work
      // **
      // this.isReady$.complete();
    });
  }

  /**
   * Get value from LD based on feature flag key
   * @param key The flag to get the value for
   * @param defaultValue The default value of the flag, to be used if the value is not available from LD
   * @returns The value retrieved from LD
   */
  public getFeatureFlag<T = LDVariation>(key: ELaunchDarklyFlag, defaultValue: T): T {
    return this.client.variation(key, defaultValue);
  }

  /**
   * Listen for changes to flags in real-time and execute a callback function
   * @param key The flag to watch
   * @param callback function called when flag is updated in LD
   */
  public watchForChanges<T = LDVariation>(key: ELaunchDarklyFlag, callback?: (newValue: T, previousValue: T) => void): void {
    this.client.on(`change:${key}`, (newValue: T, previousValue: T) => {
      // update the BehaviorSubject
      const values = this.flags$.value;
      values[key] = newValue as any;
      this.flags$.next(values);

      if (callback) {
        callback(newValue, previousValue);
      }
    });
  }

  /**
   * Close the LD connection
   */
  public async shutDown(): Promise<void> {
    await this.client?.close();
  }

  public getFlagValue(flagName: string): boolean {
    return this.flags$.getValue()[flagName];
  }
}

// for unit tests
export class MockLaunchDarklyService {
  public readonly flags$ = new BehaviorSubject({});

  watchForChanges(): void {
    // stub method
  }

  getFlagValue(): void {
    // stub method
  }
}
